import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, MessageCircle, RefreshCw, Share2, UserPlus, Users } from "lucide-react";
import { getModeratorToken } from "../lib/dailyNewsApi";
import { AdminSiteStats } from "./AdminSiteStats";

const API = process.env.REACT_APP_BACKEND_URL || "";

const EVENT_LABELS = {
  webapp_open: "Aperture web app",
  registration: "Nuovi profili",
  post: "Messaggi pubblicati",
  comment: "Commenti",
  reaction: "Reazioni",
  follow: "Nuovi follow",
  share: "Diffusioni",
};

const SOURCE_LABELS = {
  "youvoice-social": "youvoice.social",
  "shared-link": "Link a messaggi",
  external: "Siti esterni",
  direct: "Accesso diretto",
};

function MetricCard({ icon: Icon, label, value, detail }) {
  return (
    <article className="card p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-cream p-2"><Icon size={20} className="text-primary" /></span>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wider font-bold text-muted">{label}</p>
          <strong className="block text-2xl sm:text-3xl mt-1">{Number(value || 0).toLocaleString("it-IT")}</strong>
          {detail && <p className="text-xs text-muted mt-1">{detail}</p>}
        </div>
      </div>
    </article>
  );
}

export function AdminLaunchStats() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setState("loading");
    setError("");
    try {
      const response = await axios.get(`${API}/api/v1/admin/launch-stats`, {
        headers: { "x-moderator-token": getModeratorToken() },
        timeout: 10000,
      });
      setData(response.data);
      setState("ready");
    } catch (err) {
      setError(err?.response?.status === 401 ? "Credenziale amministrativa non valida." : "Statistiche di lancio non disponibili.");
      setState("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (state === "loading" && !data) return <div className="card p-6 text-center text-muted">Caricamento statistiche…</div>;
  if (state === "error" && !data) return <div className="card p-4 text-primary flex gap-2"><AlertTriangle size={18} /> {error}</div>;

  const today = data?.today || {};
  const week = data?.last7Days || {};
  const totals = data?.totals || {};
  const startedAt = data?.startedAt ? new Date(data.startedAt).toLocaleString("it-IT") : "—";

  return (
    <section>
      <div className="flex items-start gap-3 mb-4">
        <Activity className="text-primary shrink-0" size={22} />
        <div>
          <h2 className="text-xl">Statistiche di lancio</h2>
          <p className="text-xs text-muted">Misurazione separata dalle funzioni dell'app. Raccolta avviata: {startedAt}.</p>
        </div>
        <button type="button" onClick={load} className="ml-auto p-2" aria-label="Aggiorna statistiche">
          <RefreshCw size={18} className={state === "loading" ? "animate-spin" : ""} />
        </button>
      </div>

      {error && <div className="card p-3 mb-4 text-sm text-primary">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <MetricCard icon={Activity} label="Aperture oggi" value={today.webapp_open} detail={`${Number(week.webapp_open || 0).toLocaleString("it-IT")} negli ultimi 7 giorni`} />
        <MetricCard icon={UserPlus} label="Nuovi profili oggi" value={today.registration} detail={`${Number(week.registration || 0).toLocaleString("it-IT")} negli ultimi 7 giorni`} />
        <MetricCard icon={MessageCircle} label="Messaggi oggi" value={today.post} detail={`${Number(week.post || 0).toLocaleString("it-IT")} negli ultimi 7 giorni`} />
        <MetricCard icon={MessageCircle} label="Commenti oggi" value={today.comment} detail={`${Number(week.comment || 0).toLocaleString("it-IT")} negli ultimi 7 giorni`} />
      </div>

      <AdminSiteStats data={data?.site} />

      <div className="mt-8">
        <h3 className="text-lg mb-3">Totali attuali della piattaforma</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <MetricCard icon={Users} label="Profili" value={totals.users} />
          <MetricCard icon={MessageCircle} label="Messaggi" value={totals.posts} />
          <MetricCard icon={MessageCircle} label="Commenti" value={totals.comments} />
          <MetricCard icon={Activity} label="Reazioni" value={totals.reactions} />
          <MetricCard icon={UserPlus} label="Follow" value={totals.follows} />
          <MetricCard icon={Share2} label="Diffusioni" value={totals.shares} />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <section className="card p-4 sm:p-5">
          <h3 className="text-lg">Origine aperture web app</h3>
          <p className="text-xs text-muted mt-1">Non vengono salvati IP, email o identificatori personali.</p>
          <div className="mt-4 space-y-2">
            {(data?.openSources || []).length === 0 && <p className="text-sm text-muted">Nessuna apertura registrata dopo l'attivazione.</p>}
            {(data?.openSources || []).map((item) => (
              <div key={item.source} className="flex items-center justify-between gap-3 rounded-xl border border-edge px-3 py-2">
                <span className="text-sm">{SOURCE_LABELS[item.source] || item.source}</span>
                <strong>{Number(item.count || 0).toLocaleString("it-IT")}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="card p-4 sm:p-5 overflow-x-auto">
          <h3 className="text-lg">Ultimi 14 giorni</h3>
          <table className="w-full mt-3 text-sm">
            <thead><tr className="text-left text-xs text-muted"><th className="py-2 pr-3">Giorno</th><th className="py-2 px-2">Aperture</th><th className="py-2 px-2">Profili</th><th className="py-2 px-2">Post</th><th className="py-2 pl-2">Commenti</th></tr></thead>
            <tbody>
              {(data?.daily || []).map((row) => (
                <tr key={row.day} className="border-t border-edge">
                  <td className="py-2 pr-3 whitespace-nowrap">{new Date(`${row.day}T12:00:00Z`).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}</td>
                  <td className="py-2 px-2">{row.webappOpen}</td>
                  <td className="py-2 px-2">{row.registration}</td>
                  <td className="py-2 px-2">{row.post}</td>
                  <td className="py-2 pl-2">{row.comment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <section className="card p-4 sm:p-5 mt-4">
        <h3 className="text-lg">Attività dal lancio</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
          {Object.entries(EVENT_LABELS).map(([key, label]) => (
            <div key={key} className="rounded-xl border border-edge p-3">
              <span className="block text-xs text-muted">{label}</span>
              <strong className="text-lg">{Number(data?.sinceLaunch?.[key] || 0).toLocaleString("it-IT")}</strong>
            </div>
          ))}
        </div>
      </section>
    </section>
  );
}
